import { useEffect, useRef } from "react";
import {
  getMissingProfileFields,
  markProfileNudgeShown,
  shouldPresentProfileNudge,
} from "@/lib/backend/profile-completeness";
import { useMe } from "@/lib/hooks/use-me";
import { useSelectorModalStore } from "@/lib/store/modal";
import { logger } from "@/logger";

/**
 * Presents the profile-completion nudge (#383) once when the app opens, if the backend reports the
 * employee's profile is incomplete AND the nudge interval has elapsed. Reuses the shared
 * modal-store popup mechanism and records that the nudge was shown (via `markProfileNudgeShown`) so
 * it respects the interval before re-appearing. Fires at most once per mount to avoid loops.
 */
export function useProfileNudge() {
  const { me, isLoading } = useMe();
  const present = useSelectorModalStore.use.present();
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (isLoading || hasShownRef.current) {
      return;
    }
    if (!shouldPresentProfileNudge(me)) {
      return;
    }

    hasShownRef.current = true;

    present("profileNudge", { missingFields: getMissingProfileFields(me) });

    // Stamp "shown" server-side so the interval is respected next time. Failure
    // is non-fatal — the nudge simply may re-appear on the next open.
    void markProfileNudgeShown().catch((error) => {
      logger.error("Failed to mark profile nudge as shown", error);
    });
  }, [me, isLoading, present]);
}
